import { AGENT_ROLES } from './calculateFee';

/**
 * Strip the "ROLE_" prefix so both "ROLE_AGENT" and "AGENT" match.
 */
function normalizeRole(role: string): string {
  return role.replace(/^ROLE_/i, '').toUpperCase();
}

/**
 * Check if the user's userRestaurantId includes the current restaurant.
 * The backend returns userRestaurantId as int, int[], string, or null
 * depending on the user's role (see UserController.php getUserInfoAction).
 */
export function isAgentOfCurrentRestaurant(
  userRestaurantId: string | number | number[] | null | undefined,
  currentRestaurantId: number,
): boolean {
  if (userRestaurantId == null) return false;
  const ids = Array.isArray(userRestaurantId)
    ? userRestaurantId
    : String(userRestaurantId).split(',');
  return ids.map(String).includes(String(currentRestaurantId));
}

/**
 * Returns true if the user can bypass guarantee requirements:
 * - ROLE_ADMIN bypasses without restaurant affiliation
 * - Other agent roles require affiliation with the current restaurant
 */
export function canBypassGuarantee(
  roles: string[] | undefined,
  userRestaurantId: string | number | number[] | null | undefined,
  currentRestaurantId: number,
): boolean {
  if (!roles || roles.length === 0) return false;

  const normalized = roles.map(normalizeRole);

  if (normalized.includes('ADMIN')) return true;

  const hasAgentRole = normalized.some((r) => AGENT_ROLES.includes(r));
  return hasAgentRole && isAgentOfCurrentRestaurant(userRestaurantId, currentRestaurantId);
}

/**
 * Returns the first agent role found in the user's roles, or null.
 */
export function getAgentRole(roles: string[] | undefined): string | null {
  if (!roles) return null;
  for (const role of roles) {
    const normalized = normalizeRole(role);
    if (AGENT_ROLES.includes(normalized)) return role;
  }
  return null;
}
